import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CallIcon from '@mui/icons-material/Call';
import ChatIcon from '@mui/icons-material/Chat';
import LanguageIcon from '@mui/icons-material/Language';
import MailIcon from '@mui/icons-material/Mail';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import Badge from '@mui/material/Badge';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import format from 'date-fns/format';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ChannelItemDirection } from '../../../../sic-lib/enums/ChannelItemDirection';
import { ChannelItemType } from '../../../../sic-lib/enums/ChannelItemType';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        '& .MuiBadge-root > .MuiSvgIcon-root': {
            fontSize: '22px',
            fill: theme.palette.grey[500],
        },
    },
    badgeIncoming: {
        '& .MuiBadge-badge': {
            padding: 0,
            backgroundColor: theme.palette.pageNavigationBar.main,
        },
        '& .MuiBadge-badge > .MuiSvgIcon-root': {
            fontSize: '19px',
            fill: theme.palette.secondary.contrastText,
        },
    },
    badgeOutgoing: {
        '& .MuiBadge-badge': {
            padding: 0,
            backgroundColor: theme.palette.conversationTimelinePaper.light,
        },
        '& .MuiBadge-badge > .MuiSvgIcon-root': {
            fontSize: '19px',
            fill: theme.palette.secondary.contrastText,
        },
    },
}));

interface ComponentState {
    channelIcon: React.ReactElement | null;
    lastMessage: string;
}

interface Props {
    lastMessage: string;
    direction: ChannelItemDirection;
    channelType: ChannelItemType;
}

export default function UnreadMessageChannelIcon({ lastMessage, direction, channelType }: Props) {
    const { classes, cx } = useStyles();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            channelIcon: null,
            lastMessage: '',
        },
    );

    React.useEffect(() => {
        let channelIcon;

        switch (channelType) {
            case ChannelItemType.call:
                channelIcon = <CallIcon />;
                break;
            case ChannelItemType.email:
                channelIcon = <MailIcon />;
                break;
            case ChannelItemType.message:
                channelIcon = <ChatIcon />;
                break;
            case ChannelItemType.voicemail:
                channelIcon = <VoicemailIcon />;
                break;
            default:
                channelIcon = <LanguageIcon />;
        }

        compDispatch({
            channelIcon: channelIcon,
            lastMessage: format(new Date(lastMessage), 'MMM d'),
        });
    }, [channelType, lastMessage]);

    return (
        <Grid
            container
            direction="column"
            wrap="nowrap"
            alignItems="center"
            className={cx(
                classes.root,
                direction === ChannelItemDirection.incoming && classes.badgeIncoming,
                direction === ChannelItemDirection.outgoing && classes.badgeOutgoing,
            )}
        >
            <Grid item xs="auto">
                <Badge
                    badgeContent={
                        direction === ChannelItemDirection.incoming ? (
                            <ArrowDownwardIcon />
                        ) : (
                            <ArrowUpwardIcon />
                        )
                    }
                >
                    <>{compState.channelIcon}</>
                </Badge>
            </Grid>
            <Grid item xs="auto">
                <Typography component="span" variant="caption">
                    {compState.lastMessage}
                </Typography>
            </Grid>
        </Grid>
    );
}
